import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

type AppRole = Database['public']['Enums']['app_role'];

interface InvitationInfo {
  email: string;
  workspaceName: string;
  workspaceId: string;
  token: string;
  role: AppRole;
  isValid: boolean;
  isExpired: boolean;
  isAccepted: boolean;
}

interface UseInvitationResult {
  invitation: InvitationInfo | null;
  isLoading: boolean;
  error: string | null;
  acceptInvitation: () => Promise<{ success: boolean; error?: string }>;
}

export function useInvitation(): UseInvitationResult {
  const [searchParams] = useSearchParams();
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workspaceId = searchParams.get('invite');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!workspaceId || !token) {
      setInvitation(null);
      return;
    }

    const fetchInvitation = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Use RPC function to fetch invitation (bypasses RLS)
        const { data: invitationData, error: fetchError } = await supabase
          .rpc('get_invitation_by_token', {
            _workspace_id: workspaceId,
            _token: token,
          })
          .maybeSingle();

        if (fetchError) {
          logger.error('Error fetching invitation', fetchError, 'useInvitation');
          setError('Impossible de vérifier l\'invitation');
          setIsLoading(false);
          return;
        }

        if (!invitationData) {
          setError('Invitation non trouvée ou invalide');
          setIsLoading(false);
          return;
        }

        const isExpired = new Date(invitationData.expires_at) < new Date();
        const isAccepted = !!invitationData.accepted_at;

        setInvitation({
          email: invitationData.email,
          workspaceName: invitationData.workspace_name || 'Espace de travail',
          workspaceId,
          token,
          role: invitationData.role as AppRole,
          isValid: !isExpired && !isAccepted,
          isExpired,
          isAccepted,
        });
      } catch (err) {
        logger.error('Error in fetchInvitation', err, 'useInvitation');
        setError('Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [workspaceId, token]);

  const acceptInvitation = async (): Promise<{ success: boolean; error?: string }> => {
    if (!invitation) {
      return { success: false, error: 'Invitation invalide' };
    }
    if (invitation.isExpired) {
      return { success: false, error: 'Invitation expirée' };
    }

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('accept-invitation', {
        body: {
          workspaceId: invitation.workspaceId,
          token: invitation.token,
        },
      });

      // Try to read structured error payload from the edge function response
      let payload: { success?: boolean; error?: string; expected_email?: string } | null = data ?? null;
      if (invokeError) {
        try {
          const ctx = (invokeError as { context?: { json?: () => Promise<unknown> } }).context;
          if (ctx && typeof ctx.json === 'function') {
            payload = (await ctx.json()) as typeof payload;
          }
        } catch {
          // ignore
        }
      }

      if (payload?.success) {
        return { success: true };
      }

      const code = payload?.error ?? 'unknown';
      const expected = payload?.expected_email;
      const messages: Record<string, string> = {
        not_authenticated: 'Vous devez être connecté',
        invitation_not_found: 'Invitation introuvable',
        invitation_expired: 'Invitation expirée',
        email_mismatch: expected
          ? `Cette invitation est destinée à ${expected}. Connectez-vous avec ce compte.`
          : 'Cette invitation est destinée à une autre adresse email.',
        missing_parameters: 'Paramètres manquants',
        lookup_failed: 'Impossible de vérifier l\'invitation',
        insert_failed: 'Impossible de rejoindre l\'espace',
        internal_error: 'Une erreur est survenue',
      };
      const message = messages[code] ?? 'Impossible de rejoindre l\'espace';

      logger.error('Error accepting invitation', { code, payload }, 'useInvitation');
      return { success: false, error: message };
    } catch (err) {
      logger.error('Error accepting invitation', err, 'useInvitation');
      return { success: false, error: 'Une erreur est survenue' };
    }
  };

  return {
    invitation,
    isLoading,
    error,
    acceptInvitation,
  };
}
